import axios from 'axios';

// NOTE: environment var key had a typo (nREACT_APP...). Correcting to REACT_APP_API_URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const notifyAuthChanged = () => {
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('auth-token-changed'));
    } catch (err) {
      console.warn('Failed to dispatch auth-token-changed', err);
    }
  }
};

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle 401 errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Unauthorized, token is invalid or expired
      logout(); // Clear token and role from localStorage
      // Redirect to login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Universal login (supports email or username)
export const login = async (email, password) => {
  const formData = new URLSearchParams();
  formData.append('username', email); // OAuth2 form uses 'username' field
  formData.append('password', password);
  const response = await api.post('/auth/login', formData, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  if (response.data.access_token) {
    localStorage.setItem('token', response.data.access_token);
    localStorage.setItem('role', response.data.role);
    notifyAuthChanged();
  }
  return response.data;
};

// Self-signup (email-based, payment required)
export const signup = async (businessName, email, phone, password, options = {}) => {
  const response = await api.post('/auth/signup', {
    business_name: businessName,
    email,
    phone,
    password,
    plan: options.plan || 'starter',
    billing_cycle: options.billing_cycle || 'monthly',
    signup_mode: options.signup_mode || 'trial',
  });
  if (response.data.access_token) {
    localStorage.setItem('token', response.data.access_token);
    localStorage.setItem('role', response.data.role || 'client');
    notifyAuthChanged();
  }
  return response.data;
};

// Get current user profile
export const getMe = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// Client login
export const clientLogin = async (username, password) => {
  const formData = new URLSearchParams();
  formData.append('username', username);
  formData.append('password', password);
  const response = await api.post('/clients/login', formData, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  if (response.data.access_token) {
    localStorage.setItem('token', response.data.access_token);
    localStorage.setItem('role', 'client');
    notifyAuthChanged();
  }
  return response.data;
};
// Admin login (optional)
export const adminLogin = async (username, password) => {
  const formData = new URLSearchParams();
  formData.append('username', username);
  formData.append('password', password);
  const response = await api.post('/admin/login', formData, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  if (response.data.access_token) {
    localStorage.setItem('token', response.data.access_token);
    localStorage.setItem('role', 'admin');
    notifyAuthChanged();
  }
  return response.data;
};

// Client messages
export const fetchClientMessages = () => api.get('/clients/messages');
export const sendClientMessage = (client_phone, text) => api.post('/clients/messages', { client_phone, text });

// Client customers & conversations
export const fetchClientCustomers = () => api.get('/clients/customers');
export const fetchClientConversation = (customerId) => api.get(`/clients/conversations/${customerId}`);
export const fetchConversationState = (customerId) => api.get(`/clients/conversations/${customerId}/state`);
export const fetchEscalatedConversations = () => api.get('/clients/conversations/escalated');
export const sendClientReply = (customerId, text) => api.post(`/clients/conversations/${customerId}/reply`, { text });
export const resumeConversationAutomation = (customerId) => api.post(`/clients/conversations/${customerId}/resume-automation`);
export const markCustomerAsRead = (customerId) => api.post(`/clients/customers/${customerId}/read`);
export const deleteCustomerConversation = (customerId) => api.delete(`/clients/customers/${customerId}`);
export const createCustomer = (phone, name) => api.post('/clients/customers', { phone, name });

// Admin functionality
export const createClient = (clientData) => api.post('/admin/clients', clientData);
export const fetchAdminClients = () => api.get('/admin/clients');
export const fetchAdminClient = (clientId) => api.get(`/admin/clients/${clientId}`);
export const deleteClient = (clientId) => api.delete(`/admin/clients/${clientId}`);
export const fetchAdminStats = (period = '30d') => api.get(`/admin/stats?period=${period}`);
export const fetchAdminUsage = (period = '30d') => api.get(`/admin/usage?period=${period}`);
export const suspendClientAI = (clientId) => api.post(`/admin/clients/${clientId}/ai/suspend`);
export const resumeClientAI = (clientId) => api.post(`/admin/clients/${clientId}/ai/resume`);
export const saveCustomerName = (customerId, name) => api.put(`/clients/customers/${customerId}/name`, { name }, {
  headers: { 'Content-Type': 'application/json' }
});

// Client Profile
export const fetchClientProfile = async () => {
  const response = await api.get('/clients/me');
  return response;
};
export const updateClientProfile = (data) => api.put('/clients/me', data);

// Campaigns and Templates
export const fetchTemplates = (params) => api.get('/campaigns/templates', params ? { params } : undefined);
export const createTemplate = (tpl) => api.post('/campaigns/templates', tpl);
export const sendCampaign = (templateName, recipients, options = {}) => api.post('/campaigns/send', {
  campaign_name: options.campaign_name || templateName,  // Add campaign_name field
  template_name: templateName,
  recipients,
  language: options.language || 'en_US',
  variables: options.variables || undefined,
  media_header: options.media_header || undefined,
  scheduled_at: options.scheduled_at || undefined,
});
export const listCampaigns = () => api.get('/campaigns');
export const getCampaign = (campaignId) => api.get(`/campaigns/${campaignId}`);
export const deleteCampaign = (campaignId) => api.delete(`/campaigns/${campaignId}`);
export const validateNumbers = (numbers, default_region = 'US', use_meta = false) => api.post('/campaigns/validate_numbers', { numbers, default_region, use_meta });

/** Upload file to Meta WhatsApp media; returns { id, mime_type } for campaign media_header (no public URL needed). */
export const uploadWaCampaignMedia = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/campaigns/wa-upload-media', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
};

/** Resumable upload for new template HEADER sample; returns { handle } for example.header_handle. */
export const uploadTemplateHeaderSample = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/campaigns/template-header-sample', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000,
  });
};

// Bot Flows
export const fetchBotFlows = () => api.get('/bot/flows');
export const fetchActiveBotFlow = () => api.get('/bot/flows/active');
export const createBotFlow = (flow) => api.post('/bot/flows', flow);
export const updateBotFlow = (flowId, flow) => api.put(`/bot/flows/${flowId}`, flow);
export const activateBotFlow = (flowId) => api.post(`/bot/flows/${flowId}/activate`);
export const deactivateBot = () => api.post('/bot/flows/deactivate');
export const deleteBotFlow = (flowId) => api.delete(`/bot/flows/${flowId}`);
export const testBotFlow = (message, currentNodeId, flow = null) =>
  api.post('/bot/flows/test', { message, current_node_id: currentNodeId, flow });
export const fetchBotSettings = () => api.get('/bot/settings');

// Analytics
export const fetchAnalyticsSummary = (period = '30d') => api.get(`/analytics/summary?period=${period}`);
export const fetchAnalyticsTrends = (period = '30d', granularity = 'daily') => api.get(`/analytics/trends?period=${period}&granularity=${granularity}`);
export const fetchAnalyticsCampaigns = (period = '30d') => api.get(`/analytics/campaigns?period=${period}`);
export const fetchAnalyticsUsage = (period = '30d') => api.get(`/analytics/usage?period=${period}`);
export const fetchAnalyticsCustomers = (period = '30d') => api.get(`/analytics/customers?period=${period}`);

// Billing & Subscriptions
export const fetchBillingUsage = (params) => api.get('/billing/usage', params ? { params } : undefined);
export const fetchBillingInvoices = () => api.get('/billing/invoices');
export const createRazorpayOrder = (payload) => api.post('/billing/create-order', payload);
export const cancelRazorpayOrder = (payload) => api.post('/billing/cancel-order', payload);
export const verifyRazorpayPayment = (payload) => api.post('/billing/verify-payment', payload);
export const fetchAIUsage = () => api.get('/billing/ai-usage');
export const createAITopupOrder = (payload) => api.post('/billing/ai-topup/create-order', payload);
export const verifyAITopupPayment = (payload) => api.post('/billing/ai-topup/verify-payment', payload);
export const overrideSubscription = (clientId, overrideData) => api.post(`/billing/admin/clients/${clientId}/override`, overrideData);

// OAuth
export const connectFacebook = () => api.get('/oauth/connect/facebook');
export const getEmbeddedSignupConfig = () => api.get('/oauth/embedded-signup/config');
export const completeEmbeddedSignup = (payload) => api.post('/oauth/embedded-signup/exchange', payload);
export const disconnectFacebook = () => api.post('/oauth/disconnect/facebook');
export const switchPhoneNumber = (phoneNumberId) => api.post('/oauth/switch-phone-number', null, {
  params: { phone_number_id: phoneNumberId }
});

// Google Calendar Integration
export const connectGoogleCalendar = () => api.get('/integrations/google/connect');
export const disconnectGoogleCalendar = () => api.post('/integrations/google/disconnect');
export const getCalendarStatus = () => api.get('/integrations/google/status');
export const getMeetingSettings = () => api.get('/integrations/meeting-settings');
export const updateMeetingSettings = (data) => api.put('/integrations/meeting-settings', data);

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('role');
  notifyAuthChanged();
};

export default api;
